/**
 * Keystone Personas — "Put yourself in their shoes"
 * Used to help hackathon participants solve for the right problem
 * by understanding real users' perspectives.
 */

export interface Persona {
  id: string;
  name: string;
  title: string;
  emoji: string;
  color: string;
  bg: string;
  prompt: string;
  protecting: string[];
  breaksDay: string[];
  reliesOn: string[];
  thinks: string[];
  voice: string;
  /** Pages from the Keystone Persona Playbook (1-indexed) */
  playbookPages: number[];
}

export const PERSONAS: Persona[] = [
  {
    id: 'rgm',
    name: 'Ryan',
    title: 'Restaurant General Manager',
    emoji: '👨‍💼',
    color: 'text-blue-700',
    bg: 'bg-blue-50 border-blue-200',
    playbookPages: [1, 2],
    prompt: 'You are a Restaurant General Manager responsible for everything that happens inside the store. Your job is not to follow systems—it\'s to keep the store stable, profitable, and moving no matter what happens.',
    protecting: [
      'A smooth shift: no chaos, no surprises',
      'Labor and food costs staying in line',
      'A team that can execute without constant intervention',
      'Your own time—so you can lead, not troubleshoot',
    ],
    breaksDay: [
      'Systems that create extra work or need babysitting',
      'Conflicting information (POS vs KDS vs reports)',
      'Manual rework and constant corrections',
      'Surprises during rush',
    ],
    reliesOn: [
      'Own pattern recognition and experience',
      'Paper, memory, and quick checks across systems',
      'Calling or texting people to fix issues',
      'Adjusting forecasts, staffing, and prep manually',
    ],
    thinks: [
      'Are we ahead or behind?',
      'Do I trust what the system is telling me?',
      'What\'s going to break next?',
    ],
    voice: 'My job is to stabilize chaos. If the system slows me down, I work around it.',
  },
  {
    id: 'shift-lead',
    name: 'Spencer',
    title: 'Shift Lead / Supervisor',
    emoji: '⚡',
    color: 'text-amber-700',
    bg: 'bg-amber-50 border-amber-200',
    playbookPages: [3, 4],
    prompt: 'You are a Shift Lead running the store in real time. You don\'t plan the system—you make it work under pressure.',
    protecting: [
      'Speed of service during rush',
      'Order accuracy and quality',
      'Team coordination across stations',
      'A calm, controlled shift',
    ],
    breaksDay: [
      'Getting surprised (sudden rush, system lag, missing prep)',
      'Stations falling behind with no visibility',
      'Having to translate between "what the system says" and reality',
      'Manual fixes piling up during peak',
    ],
    reliesOn: [
      'Verbal callouts and constant communication',
      'Watching the line, not just screens',
      'Paper or mental tracking when systems lag',
      'Reassigning people on the fly',
    ],
    thinks: [
      'Where are we falling behind?',
      'Who needs help right now?',
      'What do we need next before it runs out?',
    ],
    voice: 'If I\'m surprised, we\'re already behind.',
  },
  {
    id: 'data-manager',
    name: 'Daz',
    title: 'Data Manager',
    emoji: '📊',
    color: 'text-purple-700',
    bg: 'bg-purple-50 border-purple-200',
    playbookPages: [5, 6],
    prompt: 'You are responsible for the accuracy of menus, pricing, promotions, and reporting across systems. You don\'t run the store—but everything breaks if your data is wrong.',
    protecting: [
      'Consistency across all systems',
      'Clean launches with no store disruption',
      'Accurate reporting and decision-making',
      'Trust in the data',
    ],
    breaksDay: [
      'Incomplete or conflicting data inputs',
      'Manual reconciliation across systems',
      'Last-minute changes that haven\'t been tested',
      'Errors that show up only after launch',
    ],
    reliesOn: [
      'Cross-checking data manually',
      'Spot testing in live environments',
      'Institutional knowledge (what usually breaks)',
      'Reactive fixes when issues escalate',
    ],
    thinks: [
      'Is this safe to launch?',
      'What breaks if I change this?',
      'Will stores feel this immediately?',
    ],
    voice: 'When I get it right, no one notices. When I get it wrong, everyone feels it.',
  },
  {
    id: 'franchise-owner',
    name: 'Frank',
    title: 'Franchise Owner',
    emoji: '🏢',
    color: 'text-green-700',
    bg: 'bg-green-50 border-green-200',
    playbookPages: [7, 8],
    prompt: 'You own multiple stores and care about performance across all of them. You are not in the store every day—you depend on signals, not firsthand visibility.',
    protecting: [
      'Profitability across locations',
      'Predictable operations without constant intervention',
      'Clear ROI on any investment',
      'Confidence in long-term growth decisions',
    ],
    breaksDay: [
      'Too many systems that don\'t align',
      'Reports that don\'t tell a clear story',
      'Initiatives that increase cost without clear return',
      'Having to manually piece together performance',
    ],
    reliesOn: [
      'Spreadsheets and custom dashboards',
      'In-person visits and gut checks',
      'Cross-checking multiple reports',
      'Local operator feedback',
    ],
    thinks: [
      'What\'s actually happening across my stores?',
      'Which stores need attention—and why?',
      'Is this investment worth it?',
    ],
    voice: 'I need visibility across all my stores—without having to chase it.',
  },
  {
    id: 'area-coach',
    name: 'Annya',
    title: 'Above-Store Leader / Area Coach',
    emoji: '🗺️',
    color: 'text-teal-700',
    bg: 'bg-teal-50 border-teal-200',
    playbookPages: [9, 10],
    prompt: 'You oversee multiple stores and are responsible for performance, consistency, and coaching. You don\'t run the shift—you diagnose patterns and fix them through people.',
    protecting: [
      'Consistency across stores',
      'Early detection of problems',
      'Strong, capable RGMs',
      'Reliable data to guide coaching',
    ],
    breaksDay: [
      'Inconsistent execution across locations',
      'Data that doesn\'t reflect reality',
      'Not knowing why a store is underperforming',
      'Spending time reacting instead of coaching',
    ],
    reliesOn: [
      'Store visits to validate what data says',
      'Personal pattern recognition across locations',
      'Parallel tracking (notes, spreadsheets)',
      'Conversations with RGMs',
    ],
    thinks: [
      'Which stores need attention?',
      'Is this a people issue or a system issue?',
      'What pattern am I seeing across locations?',
    ],
    voice: 'Data tells part of the story. The store tells the rest.',
  },
  {
    id: 'team-member',
    name: 'Tina',
    title: 'Team Member',
    emoji: '🧑‍🍳',
    color: 'text-rose-700',
    bg: 'bg-rose-50 border-rose-200',
    playbookPages: [11, 12],
    prompt: 'You are a frontline team member working a station (POS, kitchen, drive-thru, etc.). You don\'t manage the system—you execute tasks quickly and correctly under pressure.',
    protecting: [
      'Not making mistakes',
      'Keeping up with the pace',
      'Understanding what to do next',
      'Avoiding getting overwhelmed',
    ],
    breaksDay: [
      'Confusing screens or too many steps',
      'Not knowing what to do next',
      'Learning while the store is busy',
      'Fixing preventable mistakes',
    ],
    reliesOn: [
      'Watching others and copying',
      'Asking coworkers or shift leads',
      'Slowing down to avoid errors',
      'Guessing when unsure',
    ],
    thinks: [
      'What do I do next?',
      'Did I do that right?',
      'Should I ask or just try?',
    ],
    voice: 'If I don\'t understand, I won\'t ask—I\'ll just guess.',
  },
];

/**
 * Get a persona by ID.
 */
export function getPersona(id: string): Persona | undefined {
  return PERSONAS.find((p) => p.id === id);
}

/**
 * Get personas relevant to a hackathon theme.
 */
export function getPersonasForTheme(themeId: string): Persona[] {
  const themeMap: Record<string, string[]> = {
    'reinvent-restaurant-ops': ['rgm', 'shift-lead', 'team-member'],
    'reimagine-frontline-xp': ['shift-lead', 'team-member', 'rgm'],
    'reinvent-digital-ordering': ['team-member', 'rgm', 'franchise-owner'],
    'reinvent-build-and-run': ['data-manager', 'rgm', 'area-coach'],
  };
  const ids = themeMap[themeId] ?? PERSONAS.map((p) => p.id);
  return ids.map((id) => PERSONAS.find((p) => p.id === id)!).filter(Boolean);
}
